import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import {
  shouldSendElectronicDocumentToBai,
  shouldSendElectronicDocumentToVerifactu,
} from 'modules/Business/businessUtils';
import { selectCurrentBusiness } from 'modules/Business/Current/selectors';
import { Contact } from 'modules/Contacts/models';
import { ROUTES as BAI_ROUTES } from 'pages/documents/routesMap';
import { ROUTES as VERIFACTU_ROUTES } from 'pages/verifactu/routesMap';

import { CreateDocumentPayload } from '../models/createDocumentPayload';
import { Document, DocumentKind, DocumentStatus } from '../models/document';
import { CreateReceiptPayload } from '../Receipts/services/createReceipt';
import { UpdateDocumentPayload } from '../Update/services/updateDocument';

interface Params {
  documentKind?: DocumentKind;
  isVerifactu?: boolean;
}

const exportRoutes = (isVerifactu: boolean) => {
  return isVerifactu
    ? {
        SUBMISSION: VERIFACTU_ROUTES.VERIFACTU_SUBMISSION,
        SUBMISSION_TEST: VERIFACTU_ROUTES.VERIFACTU_SUBMISSION_TEST,
        RECEIPT_SUBMISSION: VERIFACTU_ROUTES.VERIFACTU_RECEIPT_SUBMISSION,
      }
    : {
        SUBMISSION: BAI_ROUTES.DOCUMENTS_BAI_SUBMISSION,
        SUBMISSION_TEST: BAI_ROUTES.DOCUMENTS_BAI_SUBMISSION_TEST,
        RECEIPT_SUBMISSION: BAI_ROUTES.DOCUMENTS_BAI_RECEIPT_SUBMISSION,
      };
};

export const useElectronicProviderSubmissionNavigation = ({
  documentKind,
  isVerifactu = false,
}: Params) => {
  const history = useHistory();

  const business = useSelector(selectCurrentBusiness);
  const shouldSendElectronicProviderToBai =
    shouldSendElectronicDocumentToBai(business);

  const shouldSendElectronicProviderToVerifactu =
    shouldSendElectronicDocumentToVerifactu(business);

  const isTest = documentKind === DocumentKind.TEST;
  const routes = exportRoutes(isVerifactu);

  const shouldNavigateToElectronicProvider = (
    status?: DocumentStatus | string | null
  ) => {
    if (isTest) {
      return true;
    }

    const shouldSendElectronicProvider =
      shouldSendElectronicProviderToVerifactu ||
      shouldSendElectronicProviderToBai;

    return shouldSendElectronicProvider && status === DocumentStatus.ISSUED;
  };

  const actionQuery = ({
    isVoid,
    isFix,
  }: {
    isVoid: boolean;
    isFix: boolean;
  }): string => {
    let action = '';
    if (isVoid) {
      action = `?action=${DocumentStatus.VOID}`;
    }
    if (isFix) {
      action = `?action=FIX`;
    }
    return action;
  };

  const navigateToElectronicProvider = (
    document:
      | CreateDocumentPayload
      | UpdateDocumentPayload
      | CreateReceiptPayload,
    contact?: Contact,
    isVoid: boolean = false,
    isFix: boolean = false
  ) => {
    const route = isTest
      ? routes.SUBMISSION_TEST // ROUTES.DOCUMENTS_BAI_SUBMISSION_TEST
      : `${routes.SUBMISSION}${actionQuery({ isVoid, isFix })}`; // ROUTES.DOCUMENTS_BAI_SUBMISSION

    history.push(route, {
      document,
      contact,
    });
  };

  const navigateToElectronicProviderReceipt = (
    document: CreateReceiptPayload
  ) => {
    const route = routes.RECEIPT_SUBMISSION; // ROUTES.DOCUMENTS_BAI_RECEIPT_SUBMISSION

    history.replace(route, {
      document,
    });
  };

  const navigateToElectronicProviderReceiptResult = (document: Document) => {
    const route = routes.RECEIPT_SUBMISSION; // ROUTES.DOCUMENTS_BAI_RECEIPT_SUBMISSION

    history.replace(route, {
      document,
    });
  };

  const shouldNavigateToElectronicProviderResult = (
    hasDigitalDocument: boolean
  ) => {
    if (isTest) {
      return true;
    }
    return (
      (shouldSendElectronicProviderToBai ||
        shouldSendElectronicProviderToVerifactu) &&
      hasDigitalDocument
    );
  };

  const navigateToElectronicProviderResult = (
    document: Document | CreateDocumentPayload,
    isVoid: boolean = false,
    contact?: Contact,
    isFix: boolean = false
  ) => {
    const route = isTest
      ? routes.SUBMISSION_TEST // ROUTES.DOCUMENTS_BAI_SUBMISSION_TEST
      : `${routes.SUBMISSION}${actionQuery({ isVoid, isFix })}`; // ROUTES.DOCUMENTS_BAI_SUBMISSION

    history.replace(route, {
      document,
      contact,
    });
  };

  return {
    shouldNavigateToElectronicProvider,
    navigateToElectronicProvider,
    shouldNavigateToElectronicProviderResult,
    navigateToElectronicProviderResult,
    navigateToElectronicProviderReceipt,
    navigateToElectronicProviderReceiptResult,
  };
};
